import React, { useState, useEffect, useCallback, useRef } from "react";
import { Box, Button, CircularProgress } from "@mui/material";
import { useTranslation } from "react-i18next";
import { useAudioRecorder } from "react-audio-voice-recorder";
import { useModal } from "../ModalProvider";
import { sessionsService } from "../../../services/sessionsService";
import { AudioChunkUploader } from "../../../utils/audioChunkUploader";
import {
  pauseInactivityTimer,
  resumeInactivityTimer,
} from "../../../hooks/useInactivityTimer";
import { usePreventNavigation } from "../../../hooks";
import { PauseIcon, PlayIcon, ErrorAlertIcon } from "../../Icons";
import {
  RecordingModalContainer,
  RecordingModalTitle,
  RecordingModalSubtitle,
  TimerContainer,
  TimerText,
  ProgressContainer,
  ButtonsContainer,
  PauseButton,
  ContinueButton,
  CancelButton,
  // ProceedButton,
  WarningContainer,
  WarningIconContainer,
  WarningTextContainer,
  WarningMessage,
  WarningTitle,
} from "./RecordingSessionModal.styled";
import SaveSessionModal from "./SaveSessionModal";
import TranscriptLoadingModal from "./TranscriptLoadingModal";

interface RecordingSessionModalProps {
  sessionId: number;
  sessionType: "treatment" | "consult";
  patientId: string;
}

const MAX_DURATION = 30 * 60;

const RecordingSessionModal: React.FC<RecordingSessionModalProps> = ({
  sessionId,
  sessionType,
  patientId,
}) => {
  const { t } = useTranslation();
  const { closeModal, openModal } = useModal();
  const [elapsedTime, setElapsedTime] = useState(0);
  const [isPaused, setIsPaused] = useState(false);
  const [isLimitReached, setIsLimitReached] = useState(false);
  const [showCancelWarning, setShowCancelWarning] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const intervalRef = useRef<NodeJS.Timeout | null>(null);
  const chunkUploaderRef = useRef<AudioChunkUploader | null>(null);
  const sessionCompletedRef = useRef(false); // Track if session was completed or cancelled
  const mountTimeRef = useRef<number>(Date.now()); // Track when component mounted

  // Audio recorder hook from package
  const { startRecording, stopRecording, togglePauseResume } =
    useAudioRecorder();

  // Prevent browser back button during recording
  usePreventNavigation(true);

  const formatTime = (seconds: number): string => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${String(mins).padStart(2, "0")}:${String(secs).padStart(2, "0")}`;
  };

  const startTimer = useCallback(() => {
    if (intervalRef.current) return;
    intervalRef.current = setInterval(() => {
      setElapsedTime((prev) => {
        if (prev >= MAX_DURATION) {
          if (intervalRef.current) {
            clearInterval(intervalRef.current);
            intervalRef.current = null;
          }
          setIsLimitReached(true);
          return MAX_DURATION;
        }
        return prev + 1;
      });
    }, 1000);
  }, []);

  const stopTimer = useCallback(() => {
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }
  }, []);

  const handlePause = async () => {
    setIsPaused(true);
    stopTimer();
    togglePauseResume(); // Pause the recording

    // Pause chunk uploader
    if (chunkUploaderRef.current) {
      chunkUploaderRef.current.pause();
    }

    try {
      await sessionsService.pauseSession(sessionId);
      console.log("Session paused via API");
    } catch (error) {
      console.error("Error pausing session:", error);
    }
  };

  const handleContinue = async () => {
    setIsPaused(false);
    startTimer();
    togglePauseResume(); // Resume the recording

    // Resume chunk uploader
    if (chunkUploaderRef.current) {
      chunkUploaderRef.current.resume();
    }

    try {
      await sessionsService.resumeSession(sessionId);
      console.log("Session resumed via API");
    } catch (error) {
      console.error("Error resuming session:", error);
    }
  };

  const handleCancelClick = () => {
    // First click - show warning
    setShowCancelWarning(true);
  };

  const handleConfirmCancel = async () => {
    // Confirm cancellation
    stopTimer();
    stopRecording();

    // Clean up chunk uploader without uploading
    if (chunkUploaderRef.current) {
      chunkUploaderRef.current.cleanup();
    }

    try {
      await sessionsService.cancelSession(sessionId);
      sessionCompletedRef.current = true; // Mark session as completed
      console.log("Session cancelled via API");
    } catch (error) {
      console.error("Error cancelling session:", error);
    }

    closeModal("recording-session-modal");
  };

  const handleKeepRecording = () => {
    // Cancel the cancellation, return to previous state
    setShowCancelWarning(false);
  };

  const handleProceed = async () => {
    // Mark session as completed FIRST to prevent cancel on unmount
    sessionCompletedRef.current = true;

    setIsSubmitting(true);
    stopTimer();
    stopRecording();

    try {
      // Stop chunk uploader and upload audio
      let blockIds: string[] = [];
      if (chunkUploaderRef.current) {
        console.log("Stopping chunk uploader and uploading audio...");
        await chunkUploaderRef.current.stop(); // Upload happens here
        blockIds = chunkUploaderRef.current.getBlockIds();
        console.log("Uploaded blockIds:", blockIds);
      }

      // Complete audio upload - server will start generating transcript
      console.log("Completing audio upload...");
      const finalBlob = await sessionsService.completeAudioUpload(
        sessionId,
        blockIds,
      );
      console.log("Audio upload completed, transcript generation started");

      // Close recording modal and show loading modal
      closeModal("recording-session-modal");
      openModal({
        id: "transcript-loading-modal",
        title: "",
        content: (
          <TranscriptLoadingModal
            sessionId={sessionId}
            onComplete={() => {
              closeModal("transcript-loading-modal");

              // Open save modal
              openModal({
                id: "save-session-modal",
                title: "",
                content: (
                  <SaveSessionModal
                    sessionId={sessionId}
                    sessionType={sessionType}
                    patientId={patientId}
                    duration={elapsedTime}
                    audioBlob={finalBlob}
                  />
                ),
                showCloseButton: false,
                maxWidth: "sm",
                fullWidth: true,
                containerPadding: "40px",
                contentPadding: "0",
                closeOnBackdropClick: false,
              });
            }}
            onCancel={() => {
              // User cancelled waiting for transcript
              console.log("User cancelled transcript loading");
              closeModal("transcript-loading-modal");
              // TODO: Maybe show notification that transcript will be available later
            }}
          />
        ),
        showCloseButton: false,
        maxWidth: "sm",
        fullWidth: true,
        containerPadding: "40px",
        contentPadding: "0",
        closeOnBackdropClick: false,
      });
    } catch (error) {
      console.error("Error completing audio upload:", error);
      // Reset flag if error occurred
      sessionCompletedRef.current = false;
      // TODO: Show error to user
    } finally {
      setIsSubmitting(false);
    }
  };

  // Initialize chunk uploader when component mounts
  useEffect(() => {
    const initChunkUploader = async () => {
      if (!chunkUploaderRef.current) {
        console.log("Initializing chunk uploader...");
        chunkUploaderRef.current = new AudioChunkUploader(sessionId);
        try {
          await chunkUploaderRef.current.start();
        } catch (error) {
          console.error("Failed to start chunk uploader:", error);
        }
      }
    };

    initChunkUploader();
  }, [sessionId]);

  // Pause inactivity timer during recording session
  useEffect(() => {
    console.log("Pausing inactivity timer for recording session...");
    pauseInactivityTimer();

    return () => {
      console.log("Resuming inactivity timer after recording session...");
      resumeInactivityTimer();
    };
  }, []);

  // Start recording when component mounts
  useEffect(() => {
    const mountTime = mountTimeRef.current; // Copy to local variable for cleanup
    console.log("Starting recording...");
    startTimer();
    startRecording();

    return () => {
      console.log("Cleaning up...");
      stopTimer();
      stopRecording();

      // Clean up chunk uploader without uploading
      if (chunkUploaderRef.current) {
        chunkUploaderRef.current.cleanup();
      }

      // Only cancel if session was not completed AND modal lived for more than 500ms
      // This prevents accidental cancel if modal was closed immediately
      const componentLifetime = Date.now() - mountTime;
      if (!sessionCompletedRef.current && componentLifetime > 500) {
        console.log(
          `Session not completed, cancelling... (lifetime: ${componentLifetime}ms)`,
        );
        sessionsService.cancelSession(sessionId).catch((error) => {
          console.error("Error cancelling session on unmount:", error);
        });
      } else if (!sessionCompletedRef.current) {
        console.log(
          `Skipping cancel - component lifetime too short: ${componentLifetime}ms`,
        );
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Stop recording when limit is reached
  useEffect(() => {
    if (isLimitReached) {
      console.log("Recording limit reached, stopping recording...");
      stopRecording();

      // Pause chunk uploader (don't stop it - we still need to upload when user clicks Proceed)
      if (chunkUploaderRef.current) {
        chunkUploaderRef.current.pause();
      }
    }
  }, [isLimitReached, stopRecording]);

  const progress = (elapsedTime / MAX_DURATION) * 100;
  const isWarning = isLimitReached;

  const modalTitle =
    sessionType === "treatment"
      ? t("recordingSession.treatment.title")
      : t("recordingSession.consult.title");

  const modalSubtitle =
    sessionType === "treatment"
      ? t("recordingSession.treatment.subtitle")
      : t("recordingSession.consult.subtitle");

  return (
    <RecordingModalContainer>
      <RecordingModalTitle>{modalTitle}</RecordingModalTitle>
      <RecordingModalSubtitle>{modalSubtitle}</RecordingModalSubtitle>

      <ProgressContainer>
        <Box sx={{ position: "relative", display: "inline-flex" }}>
          {/* Background circle */}
          <CircularProgress
            variant="determinate"
            value={100}
            size={290}
            thickness={1.5}
            sx={{
              color: "#B9CED5",
              position: "absolute",
            }}
          />
          {/* Progress circle */}
          <CircularProgress
            variant="determinate"
            value={progress}
            size={290}
            thickness={1.5}
            sx={{
              color: isWarning ? "#D32F2F" : "#3A8AB9",
              "& .MuiCircularProgress-circle": {
                strokeLinecap: "round",
              },
            }}
          />
        </Box>
        <TimerContainer>
          <TimerText isWarning={isWarning}>{formatTime(elapsedTime)}</TimerText>
          {!isLimitReached && !isPaused && (
            <PauseButton onClick={handlePause}>
              <PauseIcon width="20" height="20" />
              {t("recordingSession.pause")}
            </PauseButton>
          )}
          {isPaused && !isLimitReached && (
            <ContinueButton onClick={handleContinue}>
              <PlayIcon width="20" height="20" />
              {t("recordingSession.continue")}
            </ContinueButton>
          )}
        </TimerContainer>
      </ProgressContainer>

      {isLimitReached && (
        <WarningContainer>
          <WarningIconContainer>
            <ErrorAlertIcon width="24" height="24" />
          </WarningIconContainer>
          <WarningTextContainer>
            <WarningTitle>
              {t("recordingSession.limitReached.title")}
            </WarningTitle>
            <WarningMessage>
              {t("recordingSession.limitReached.message")}
            </WarningMessage>
          </WarningTextContainer>
        </WarningContainer>
      )}

      {showCancelWarning && !isLimitReached && (
        <WarningContainer>
          <WarningIconContainer>
            <ErrorAlertIcon width="24" height="24" />
          </WarningIconContainer>
          <WarningTextContainer>
            <WarningTitle>
              {t("recordingSession.cancelWarning.title")}
            </WarningTitle>
            <WarningMessage>
              {t("recordingSession.cancelWarning.message")}
            </WarningMessage>
          </WarningTextContainer>
        </WarningContainer>
      )}

      <ButtonsContainer>
        {showCancelWarning ? (
          <>
            <CancelButton onClick={handleConfirmCancel}>
              {t("recordingSession.cancelRecording")}
            </CancelButton>
            <Button variant="contained" onClick={handleKeepRecording}>
              {t("recordingSession.keepRecording")}
            </Button>
          </>
        ) : (
          <>
            <CancelButton onClick={handleCancelClick} disabled={isSubmitting}>
              {t("common.cancel")}
            </CancelButton>
            <Button variant="contained" onClick={handleProceed} disabled={isSubmitting}>
              {isSubmitting ? t("common.uploading") : t("common.proceed")}
              {isSubmitting && (
                <CircularProgress size={20} sx={{ marginLeft: 1 }} color="inherit" />
              )}
            </Button>
          </>
        )}
      </ButtonsContainer>
    </RecordingModalContainer>
  );
};

export default RecordingSessionModal;
